import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {ApDatePickerValue} from 'webcommon/core-ui';
import { FindControlIdService } from 'webcommon/legacy-common';
import { ApEditMessageDto } from 'webcommon/shared';
import { ApBaseComponent } from 'webcommon/core-ui';
import { ApNotificationService } from 'webcommon/core-ui';
import { ApEditorMessageButtonsDto } from './ApEditorMessageButtonsDto';
import { ApHtmlTextEditorComponent } from 'webcommon/html-editor';

@Component({
  selector: 'ap-message-editor',
  styleUrls: ['./ap-message-editor.component.less'],
  templateUrl: './ap-message-editor.component.html',
})
export class ApMessageEditorComponent extends ApBaseComponent implements OnInit {
  @Input() baseId?: string;
  @Input() readOnly: boolean;
  @Input() showButtons: ApEditorMessageButtonsDto;
  @Input() clearMessageOnSend: boolean;

  private _urgencyId: string | null = null;
  @Input()
  set urgencyId(val) {
    this._urgencyId = val;
  }
  get urgencyId() {
    return this._urgencyId;
  }
  @Output() urgencyIdChange = new EventEmitter<string | null>();

  private _dueDate: ApDatePickerValue = undefined;
  @Input()
  set dueDate(val) {
    this._dueDate = val;
  }
  get dueDate() {
    return this._dueDate;
  }
  @Output() dueDateChange = new EventEmitter<ApDatePickerValue>();

  @Input() completeCallback: (completeMessageDto: ApEditMessageDto) => Observable<boolean>;
  @Input() sendCallback: (sendMessageDto: ApEditMessageDto) => Observable<boolean>;
  @Output() editComplete: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('createMessageForm', { static: true }) createMessageForm: NgForm;
  @ViewChild('apHtmlTextEditor') apHtmlTextEditor: ApHtmlTextEditorComponent;

  Id: string;

  messageBody: string;
  messageLength = 0;
  completingMessage = false;
  sendingMessage = false;
  hasError = false;
  errorMessage = '';

  inputNames = {
    duedate: 'duedate',
  };

  constructor(
    public findControlIdService: FindControlIdService,
    private apNotificationService: ApNotificationService,
  ) {
    super();
  }

  ngOnInit() {
    this.loading = true;
    this.setId();
    this.load();
  }

  private setId() {
    this.Id = `${this.baseId}-text`;
  }

  private load() {
    this.loading = false;
  }

  onMessageUrgencyChange(urgencyId: string | null) {
    this.urgencyIdChange.emit(urgencyId);
  }

  complete() {
    this.completingMessage = true;
    const completeMessageRequest: ApEditMessageDto = {
      DueDate: this.dueDate,
      UrgencyId: this.urgencyId ?? undefined,
    };
    this.completeCallback(completeMessageRequest).pipe(
      finalize(() => this.completingMessage = false),
      this.takeUntilDestroy(),
    ).subscribe(
      () => {
        this.hasError = false;
        this.editComplete.emit(true);
      },
      () => {
        this.hasError = true;
      },
    );
  }

  send() {
    this.sendingMessage = true;
    const content = this.apHtmlTextEditor.getContent();
    const sendMessageRequest: ApEditMessageDto = {
      Body: content,
      DueDate: this.dueDate,
      UrgencyId: this.urgencyId ?? undefined,
    };
    this.sendCallback(sendMessageRequest).pipe(
      finalize(() => this.sendingMessage = false),
      this.takeUntilDestroy(),
    ).subscribe(
      () => {
        if (this.clearMessageOnSend) {
          this.messageBody = '';
          this.onTextContentChanged(this.messageBody);
        }
        this.hasError = false;
        this.editComplete.emit(true);
        this.apNotificationService.success('Message sent successfully');
      },
      () => {
        this.hasError = true;
      },
    );
  }

  cancel() {
    if (this.editComplete) {
      this.editComplete.emit(false);
    }
  }

  updateDueDate(value: ApDatePickerValue): void {
    const control = this.createMessageForm.controls[this.inputNames.duedate];
    this.dueDateChange.emit(value);
    control.markAsDirty();
  }

  onTextContentChanged(value: string): void {
    this.messageLength = value.replace(/(\r\n|\n|\r)/gm, '').replace(/\s/g, '').length;
  }
}
